import { useCallback, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  asRecord,
  asList,
  hasValue,
  erased,
  type V2Record,
} from "./AllMattersV2Fields";
import { AllMattersV2Overview } from "./AllMattersV2Overview";
import { AllMattersV2Title } from "./AllMattersV2Title";
import { AllMattersV2Register } from "./AllMattersV2Register";
import { TrustCatalogs } from "./AllMattersV2Catalogs";
import { JointSecurityCatalogs } from "./AllMattersV2JointSecurity";
export { asRecord, type V2Record } from "./AllMattersV2Fields";

/** A section that failed, wholly or in part, keeps its tab and says so. */
const failure = (status: unknown): string | null => {
  const item = asRecord(status);
  if (item.状態 !== "失敗" && item.状態 !== "一部失敗") return null;
  const reason = typeof item.理由 === "string" && item.理由 ? item.理由 : "";
  return `${
    item.状態 === "失敗" ? "解析できませんでした" : "一部を解析できませんでした"
  }${reason ? `（${reason}）` : ""}。登記の PDF を確認してください。`;
};

export const AllMattersV2Result = ({
  result,
  notice,
  onShowPage,
}: {
  result: V2Record;
  /** Shown above the overview, below the sticky section tabs. */
  notice?: ReactNode;
  onShowPage: (page: number) => void;
}): JSX.Element => {
  const [selected, setSelected] = useState("概要");
  const [showErased, setShowErased] = useState(true);
  const [focusCatalog, setFocusCatalog] = useState<number | null>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const stickyHeight = useCallback(
    () => stickyRef.current?.offsetHeight ?? 0,
    []
  );
  const clearFocus = useCallback(() => {
    setFocusCatalog(null);
  }, []);
  const views = asRecord(result.ビュー);
  const rights = asRecord(result.権利部);
  const jointCatalogs = asList(rights.共同担保目録);
  // References from 乙区 and the overview open the catalog tab at that catalog.
  const openJointCatalog = (index: number): void => {
    if (!jointCatalogs[index]) return;
    if (erased(jointCatalogs[index])) setShowErased(true);
    setSelected("共同担保目録");
    setFocusCatalog(index);
  };
  const sections: Array<[string, unknown]> = [
    ["表題部", result.表題部],
    ["甲区", asRecord(rights.甲区).登記],
    ["乙区", asRecord(rights.乙区).登記],
    ["共同担保目録", rights.共同担保目録],
    ["信託目録", rights.信託目録],
  ];
  const statuses = asRecord(result.解析状態);
  const available = sections.filter(
    ([name, value]) => hasValue(value) || failure(statuses[name]) != null
  );
  const active =
    selected === "概要" || available.some(([name]) => name === selected)
      ? selected
      : "概要";
  const current = available.find(([name]) => name === active)?.[1];
  const erasedCount = asList(current).filter(erased).length;
  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Tabs and the section heading stick together so neither hides the other. */}
      <Box
        ref={stickyRef}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          px: 2,
        }}
      >
        <Tabs
          value={active}
          onChange={(_, value: string) => {
            setSelected(value);
          }}
          aria-label="解析結果の区分の切り替え"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {["概要", ...available.map(([name]) => name)].map((name) => (
            <Tab
              key={name}
              value={name}
              label={name}
              id={`v2-section-tab-${name}`}
              aria-controls="v2-section-panel"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>
        {active !== "概要" && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
            sx={{ borderTop: 1, borderColor: "divider", py: 0.5 }}
          >
            <Stack direction="row" alignItems="baseline" gap={1}>
              <Typography variant="subtitle1" component="h3" fontWeight={700}>
                {active}
              </Typography>
              {Array.isArray(current) && (
                <Typography variant="body2" color="text.secondary">
                  {current.length}件
                  {erasedCount > 0 && `（うち抹消 ${erasedCount}件）`}
                </Typography>
              )}
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={showErased}
                  onChange={(_, checked) => {
                    setShowErased(checked);
                  }}
                />
              }
              label="抹消済み・抹消推定も表示"
              sx={{ mr: 0 }}
            />
          </Stack>
        )}
      </Box>
      <Box
        role="tabpanel"
        id="v2-section-panel"
        aria-labelledby={`v2-section-tab-${active}`}
        sx={{ minWidth: 0, overflowWrap: "anywhere", p: 2 }}
      >
        {active === "概要" ? (
          <Stack spacing={2}>
            {notice}
            <AllMattersV2Overview
              result={result}
              sections={available}
              onSelect={setSelected}
              onOpenJointCatalog={openJointCatalog}
              onShowPage={onShowPage}
            />
          </Stack>
        ) : (
          <>
            {failure(statuses[active]) != null && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {failure(statuses[active])}
              </Alert>
            )}
            {active === "甲区" || active === "乙区" ? (
              <AllMattersV2Register
                section={active}
                entries={asList(current)}
                currentIds={
                  asRecord(
                    active === "甲区"
                      ? views.最終所有者
                      : views.現在有効な担保権
                  ).根拠
                }
                showErased={showErased}
                onShowPage={onShowPage}
                jointCatalogs={jointCatalogs}
                onOpenJointCatalog={openJointCatalog}
              />
            ) : active === "共同担保目録" ? (
              <JointSecurityCatalogs
                catalogs={asList(current)}
                showErased={showErased}
                focusIndex={focusCatalog}
                onFocused={clearFocus}
                stickyHeight={stickyHeight}
              />
            ) : active === "信託目録" ? (
              <TrustCatalogs
                catalogs={asList(current)}
                views={views.現在の信託目録}
                showErased={showErased}
                onShowPage={onShowPage}
              />
            ) : (
              <AllMattersV2Title
                value={current}
                judgements={views.表題部現在値の判定}
                showErased={showErased}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
